import type { LicensedTerritory } from '@lottocom/lottery-mapping'

import Money from '~~/models/Money'

import type { LotteryId } from '#api/shared/@types/generated/backend/graphql-schema-types'

import { BonusCodeVerdict, getBonusCodeVerdict } from '~/utils/bonusCodeCondition'

import { EDITOR_TICKET_TYPE } from '../constants/options'
import { useEditorOptionsStore } from '../stores/options'
import { useEditorPrice } from './editorPrice'

export const useEditorPriceDetails = (lotteryId: LotteryId) => {
  const licensedTerritory = useLicensedTerritory({ raw: true })
  const isPriceTransparencyEnabled = useIsPriceTransparencyEnabled(
    licensedTerritory as LicensedTerritory,
  )

  const { currentPrice, flaggedCount, playCount } = useEditorPrice(lotteryId)

  const { coupon } = useBonusCodeCondition(currentPrice)

  const optionsStore = useEditorOptionsStore(lotteryId)

  const total = computed(() => {
    const price = currentPrice.value
    if (!price) return new Money()

    return new Money(isPriceTransparencyEnabled ? price.grossTotal : price.chargeTotal)
  })

  // only a redeemable code counts, one still waiting for its conditions is not active yet
  const hasPromoCode = computed(
    () => !!coupon.value && getBonusCodeVerdict(coupon.value) === BonusCodeVerdict.Fulfilled,
  )

  const promotionTitle = computed(() =>
    hasPromoCode.value ? (coupon.value?.info.title ?? null) : null,
  )

  const convenienceFee = computed(() => new Money(currentPrice.value?.feePerPlaySlip))

  const salesTax = computed(() => new Money(currentPrice.value?.salesTax))

  const subtotal = computed(() => new Money(currentPrice.value?.chargeTotal))

  const isSubscription = computed(() => optionsStore.ticketType === EDITOR_TICKET_TYPE.Subscription)

  const prepaidSubscriptionAmount = computed(
    () => optionsStore.prepaidSubscriptionAmount?.value ?? null,
  )

  return {
    convenienceFee,
    currentPrice,
    flaggedCount,
    hasPromoCode,
    isSubscription,
    playCount,
    prepaidSubscriptionAmount,
    promotionTitle,
    salesTax,
    subtotal,
    total,
  }
}
